import { isEmpty } from "lodash";
import {
	type GetStaticPaths,
	type GetStaticProps,
	type GetStaticPropsResult,
	type NextPage,
} from "next";
import { useEffectOnce } from "react-use";
import SuperJSON from "superjson";
import { useAnalytics } from "use-analytics";
import { createActor, waitFor } from "xstate";

import { Logger } from "@moment/logging";

import { isDesktopAppMode } from "~/auth/interop/common";
import { usePageEditorState } from "~/components/canvas/easel/usePageEditorState";
import { Page } from "~/components/page/Page";
import { SsrPage } from "~/components/page/SsrPage";
import { withAppShell } from "~/components/withAppShell";
import { appMachine } from "~/state/machines/app/app.machine";

export type PageProps = {
	workspaceName: string;
	documentId: string;
	pageId: string;
	pageSlug: string;
	documentMetadata?: string;
	node?: string;
};

/**
 * A wrapper that fetches the canvas data on the client instead of via SSR.
 */
export const PageWrapper: NextPage<PageProps> = (props) => {
	const { page } = useAnalytics();

	const { editorState } = usePageEditorState(props.documentId, props.pageId);

	// Analytics
	useEffectOnce(() => {
		page();
	});

	if (!isDesktopAppMode() && !editorState) {
		return <SsrPage {...props} />;
	}

	return <Page />;
};

const notFound = (
	error: string,
	errorContext: Record<string, unknown> = {}
): GetStaticPropsResult<PageProps> => {
	const logger = new Logger("PageWrapper [getStaticProps]");
	logger.error(error, errorContext);

	return {
		notFound: true,
		revalidate: 1,
	};
};

export const getStaticProps:
	| undefined
	| GetStaticProps<
			PageProps,
			{ workspaceName: string; documentId: string; pageSlug: string | string[] }
	  > = isDesktopAppMode()
	? undefined // Don't run this stuff in dev (desktop app doesn't have it in builds anyway)
	: async (context) => {
			try {
				if (!context.params) {
					return notFound("No params found");
				}

				const { workspaceName, documentId } = context.params;
				const pageSlug = Array.isArray(context.params.pageSlug)
					? context.params.pageSlug.join("/")
					: context.params.pageSlug;

				if (isEmpty(workspaceName) || isEmpty(documentId)) {
					return notFound("No workspaceName or documentId found", context.params);
				}

				const appActor = createActor(appMachine, {
					systemId: "app",
					input: {
						documents: {
							[documentId]: {
								id: documentId,
								title: documentId,
								createdAt: new Date(0),
								updatedAt: new Date(0),
								workspaces: [
									{
										id: `/${workspaceName}`,
										name: workspaceName,
										host: "",
										slug: workspaceName,
									},
								],
							},
						},
					},
				});

				const documentActor = appActor.getSnapshot().context.documentActorRefs[documentId];

				if (!documentActor) {
					return notFound("No documentActor found", {
						documentId,
						appActor: appActor.getPersistedSnapshot(),
					});
				}

				documentActor.start();
				documentActor.send({ type: "document.opened" });

				await waitFor(documentActor, (state) => state.matches("viewing"), {
					// If the documentActor doesn't resolve in 5 seconds, timeout
					timeout: 5_000,
				});

				let pageId: string | undefined;
				const pagesMetadata =
					documentActor.getSnapshot().context.metadata?.pagesMetadata ?? [];
				if (isEmpty(pageSlug)) {
					// If we're looking for the index, find it by the slug
					pageId =
						pagesMetadata.find((page) => page.slug === "index")?.id ??
						// or if it's not found by the slug "index", grab the very first page
						pagesMetadata[0]?.id;
				} else {
					// Find the exact match if we're not looking for index
					pageId = pagesMetadata.find((page) => page.slug === pageSlug)?.id;
				}

				if (!pageId) {
					return notFound("Unable to find page id", {
						pageSlug,
						documentActor: documentActor.getPersistedSnapshot(),
					});
				}

				const pageActor = documentActor.getSnapshot().context.pageActorRefs[pageId];
				if (!pageActor) {
					return notFound("unable to find page actor", {
						pageId,
						documentActorContext: documentActor.getSnapshot().context,
					});
				}

				pageActor.start();
				pageActor.send({ type: "page.opened" });

				try {
					await waitFor(pageActor, (state) => state.matches({ data: "editing" }), {
						// If the pageActor doesn't resolve in 5 seconds, timeout
						timeout: 5_000,
					});
				} catch (error) {
					return notFound("Error waiting for pageActor", {
						error,
						context,
					});
				}

				const props: PageProps = {
					workspaceName,
					documentId,
					pageId,
					pageSlug: isEmpty(pageSlug) ? "index" : pageSlug,
					documentMetadata: SuperJSON.stringify(
						documentActor.getSnapshot().context.metadata
					),
					node: SuperJSON.stringify(
						pageActor.getSnapshot().context.editorState?.doc.toJSON()
					),
				};

				return {
					props,
					revalidate: 60,
				};
			} catch (error) {
				return notFound("Error fetching page", {
					error,
					context,
				});
			}
		};

export const getStaticPaths: undefined | GetStaticPaths = isDesktopAppMode()
	? undefined
	: async () => {
			return {
				// Don't generate any paths at build time.
				paths: [],

				// This will block rendering until the document is fetched from the server.
				fallback: "blocking",
			};
		};

export default withAppShell(PageWrapper);
